import React, { useEffect, useState } from 'react';
import { CalendarIcon, ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';
import analyticsService from '../../services/analyticsService';
import Button from '../UI/Button';
import LoadingSpinner from '../UI/LoadingSpinner';

interface CountReportFilters {
  dietId: string;
  serviceDate: string;
  mealType: string;
}

interface IngredientCount {
  ingredientName: string;
  count: number;
}

const MEAL_TYPES = [
  { value: 'BREAKFAST', label: 'Breakfast' },
  { value: 'SNACK_AM', label: 'Snack AM' },
  { value: 'LUNCH', label: 'Lunch' },
  { value: 'SNACK_PM', label: 'Snack PM' },
  { value: 'DINNER', label: 'Dinner' },
];

const CountReport: React.FC = () => {
  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState<CountReportFilters>({
    dietId: '',
    serviceDate: today,
    mealType: 'BREAKFAST',
  });
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState<any[]>([]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Use the new chef-count endpoint that aggregates across all clients
      const res = await analyticsService.getChefCountAnalytics({
        serviceDate: filters.serviceDate,
        mealType: filters.mealType,
      });

      // Store the ingredient counts
      setAllData([{
        ingredientCounts: res.ingredientCounts,
        clientCount: res.clientCount,
      } as any]);
      setData({ ingredientCounts: res.ingredientCounts } as any);
    } catch (error) {
      console.error('Failed to load count data:', error);
      setAllData([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.mealType, filters.serviceDate]);

  // Get ingredient counts from the data
  const ingredientCounts: IngredientCount[] = allData.length > 0 && allData[0]?.ingredientCounts ? allData[0].ingredientCounts : [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Service Date</label>
          <input
            type="date"
            className="form-input w-full"
            value={filters.serviceDate}
            onChange={(e) => setFilters((prev) => ({ ...prev, serviceDate: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meal</label>
          <select
            className="form-select w-full"
            value={filters.mealType}
            onChange={(e) => setFilters((prev) => ({ ...prev, mealType: e.target.value }))}
          >
            {MEAL_TYPES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end space-x-2">
          <Button onClick={() => window.print()} variant="outline" className="flex items-center w-full justify-center">
            <PrinterIcon className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">CHEF COUNT REPORT</h2>
            <p className="text-gray-600 mt-2">
              {filters.serviceDate} • {filters.mealType} • All Active Diets
            </p>
          </div>

          {ingredientCounts.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No ingredients to prepare for today.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-4 py-3 text-left font-bold text-gray-900">#</th>
                    <th className="px-4 py-3 text-left font-bold text-gray-900">Ingredient</th>
                    <th className="px-4 py-3 text-right font-bold text-gray-900">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredientCounts.map((item, index) => (
                    <tr key={`${item.ingredientName}-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{item.ingredientName}</td>
                      <td className="px-4 py-3 text-right font-bold text-lg text-gray-900">
                        {item.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CountReport;

